import { Helmet } from 'react-helmet-async';

const SEO = ({ 
  title, 
  description, 
  keywords,
  image,
  url,
  type = 'website',
  structuredData 
}) => {
  const siteName = 'Lyfe Simply';
  const defaultDescription = 'Transform your life with expert hypnotherapy, life coaching, and professional training in Dubai. 15+ years experience. Book your session today.';
  const defaultImage = 'https://therapy-booking-cms.preview.emergentagent.com/og-image.jpg';
  const baseUrl = 'https://therapy-booking-cms.preview.emergentagent.com';
  
  const fullTitle = title ? `${title} | ${siteName}` : `${siteName} - Hypnotherapy & Life Coaching in Dubai`;
  const metaDescription = description || defaultDescription;
  const metaImage = image || defaultImage;
  // Handle URL - if it already starts with http, use as-is, otherwise prepend baseUrl
  const canonicalUrl = url ? (url.startsWith('http') ? url : `${baseUrl}${url}`) : baseUrl;

  // Default structured data for Organization
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Lyfe Simply",
    "description": metaDescription,
    "url": baseUrl,
    "logo": `${baseUrl}/logo.png`,
    "image": metaImage,
    "telephone": "+971-XX-XXX-XXXX",
    "email": "info@lyfesimply.com",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Dubai",
      "addressCountry": "AE"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "25.2048",
      "longitude": "55.2708"
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      "opens": "09:00",
      "closes": "18:00"
    },
    "priceRange": "$$",
    "sameAs": [
      "https://www.instagram.com/lyfesimply",
      "https://www.youtube.com/@lyfesimply",
      "https://www.facebook.com/lyfesimply"
    ]
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={metaDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={metaImage} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_AE" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={metaImage} />

      {/* Additional SEO */}
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow" />
      <meta name="author" content="Lyfe Simply" />
      <meta name="geo.region" content="AE-DU" />
      <meta name="geo.placename" content="Dubai" />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData || organizationSchema)}
      </script>
    </Helmet>
  );
};

export default SEO;

// Pre-built structured data generators
export const generateServiceSchema = (service) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "serviceType": service.name,
  "name": service.name,
  "description": service.description,
  "provider": {
    "@type": "LocalBusiness",
    "name": "Lyfe Simply"
  },
  "areaServed": {
    "@type": "City",
    "name": "Dubai"
  }
});

export const generateEventSchema = (event) => ({
  "@context": "https://schema.org",
  "@type": "Event",
  "name": event.title,
  "description": event.description,
  "startDate": event.date,
  "location": {
    "@type": "Place",
    "name": event.location || "Dubai",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Dubai",
      "addressCountry": "AE"
    }
  },
  "organizer": {
    "@type": "Organization",
    "name": "Lyfe Simply"
  },
  "offers": event.price ? {
    "@type": "Offer",
    "price": event.price,
    "priceCurrency": "AED"
  } : undefined
});

export const generateFAQSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

export const generateBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url
  }))
});

export const generatePersonSchema = (person) => ({
  "@context": "https://schema.org",
  "@type": "Person",
  "name": person.name,
  "jobTitle": person.title,
  "description": person.bio,
  "image": person.image_url,
  "worksFor": {
    "@type": "Organization",
    "name": "Lyfe Simply"
  }
});
